// SU commander level table loader — v0M9.2.
//
// Loads the per-level XP thresholds + unlock strings from
// src/data/generated/knowledge.json (CommanderUnlocks sheet in
// docs/KnowledgeTree.xlsx). Levels 1..10 at v1; XP curve escalates
// 100 → 10000; `xpForNextLevel = 0` at the cap (level 10). Consumed by
// the Commander XP progression backend (v0M4.0g) — this loader replaces
// the previously hardcoded xp-per-level constants.

import { assertSchemaVersion, KNOWLEDGE_SCHEMA_VERSION } from './loader';
import rawBlob from './generated/knowledge.json';

export interface CommanderLevelSpec {
  readonly level: number;
  /**
   * XP required to advance from `level` → `level + 1`. 0 at the max
   * level (10). Cumulative XP is Commander-side state — this table only
   * carries the per-level *delta*.
   */
  readonly xpForNextLevel: number;
  /**
   * Free-text unlock string, blank for pure-threshold levels
   * (e.g. levels 6-9 in Michael's authored table). Effects wire per-
   * unlock over time — matches the knowledge Effects pattern.
   */
  readonly unlocks: string;
}

export type CommanderLevels = readonly CommanderLevelSpec[];

/** Load the commander level table. Fails loudly on schemaVersion mismatch. */
export function loadCommanderLevels(): CommanderLevels {
  assertSchemaVersion(rawBlob, KNOWLEDGE_SCHEMA_VERSION, 'src/data/generated/knowledge.json');
  return (rawBlob as unknown as { commanderLevels: CommanderLevels }).commanderLevels;
}

/**
 * Retrieve the spec for a given level. Returns undefined for levels
 * outside the authored range (below 1 or above the table cap) so
 * callers can decide how to handle out-of-bounds levels — usually
 * "clamp to max" or "commander has no further progression".
 */
export function getCommanderLevel(
  levels: CommanderLevels,
  level: number,
): CommanderLevelSpec | undefined {
  return levels.find((l) => l.level === level);
}

/** Convenience: MAX level (highest entry in the table). */
export function commanderMaxLevel(levels: CommanderLevels): number {
  let max = 0;
  for (const l of levels) if (l.level > max) max = l.level;
  return max;
}

/**
 * v0M10.6 (2026-07-24) — Cumulative XP required to REACH a given level.
 * L1 = 0 (spawn level). L2 = levels[0].xpForNextLevel. L3 = L2 +
 * levels[1].xpForNextLevel. Etc.
 *
 * Undefined for levels above the table's max (Michael's cap of 10 at
 * v1 — an over-cap query returns Infinity so callers naturally can't
 * "reach" it).
 */
export function cumulativeXpForLevel(targetLevel: number, levels: CommanderLevels): number {
  if (targetLevel <= 1) return 0;
  let cumulative = 0;
  // Sort defensive: loader returns sorted, but if a future refactor changes that,
  // this helper must not silently break.
  const sorted = [...levels].sort((a, b) => a.level - b.level);
  for (const spec of sorted) {
    if (spec.level >= targetLevel) break;
    if (spec.xpForNextLevel <= 0) return Infinity; // Beyond max — unreachable
    cumulative += spec.xpForNextLevel;
  }
  return cumulative;
}

/**
 * v0M10.6 (2026-07-24) — Compute a commander's level from their
 * cumulative XP, using the xlsx-authored CommanderUnlocks table.
 *
 * Level = largest L such that `xp >= cumulativeXpForLevel(L, levels)`.
 * Capped at `commanderMaxLevel(levels)` (10 at v1).
 *
 * Consumers: battle-conclusion.ts XP grant + economy.ts stepCommanderStructureXp
 * + Army Info panel display. Single source of truth for the level curve.
 */
export function commanderLevelForXp(xp: number, levels: CommanderLevels): number {
  const sorted = [...levels].sort((a, b) => a.level - b.level);
  let level = 1;
  let cumulative = 0;
  for (const spec of sorted) {
    if (spec.xpForNextLevel <= 0) break; // Max-level row — cap.
    cumulative += spec.xpForNextLevel;
    if (xp >= cumulative) {
      level = spec.level + 1;
    } else {
      break;
    }
  }
  return Math.min(level, commanderMaxLevel(sorted));
}
